import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { clearReplyMessage } from '@/store/chat/actions';

import { selectChat, selectHasThinkingMessages, selectIsSwitchingChat } from '@/store/chat/selectors';
import { selectUsers } from '@/store/users/selectors';

import { AiOutlineSend } from 'react-icons/ai';
import { FiCornerUpLeft, FiSquare, FiX } from 'react-icons/fi';

import FileInput from './FileInput';
import ModalWithFile from './ModalWithFile';

import { useModelSelection } from '@/contexts/ModelSelectionContext';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import styles from '../Messages.module.scss';

// Простая функция генерации UUID v4
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r % 4) + 8;
    return v.toString(16);
  });
};

const Footer = ({ socket, containerRef }) => {
  const [value, setValue] = useState(undefined);
  const [filesData, setFilesData] = useState([]);

  const isSwitchingChat = useSelector(selectIsSwitchingChat);
  const { t } = useTranslation('chat');
  const { selectedUser, replyMessage, selectedChatTopic } = useSelector(selectChat);
  const { currentUser } = useSelector(selectUsers);
  const hasThinkingMessages = useSelector(selectHasThinkingMessages);
  const { selectedModelName } = useModelSelection();

  const textAreaRef = useRef();
  const dispatch = useDispatch();

  useEffect(() => {
    textAreaRef.current.focus();
  }, [replyMessage]);

  useEffect(() => {
    document.body.addEventListener('keypress', submitEnter);

    return () => {
      document.body.removeEventListener('keypress', submitEnter);
    };
  }, [value]);

  const handleClearReply = () => {
    dispatch(clearReplyMessage());
  };

  const botId =
    selectedUser?.bot?.botId || selectedUser?.botId || selectedChatTopic?.bot?.botId || selectedChatTopic?.botId;

  const handleStopAgent = () => {
    console.log('🛑 Инициирована остановка агента');

    if (!socket) {
      console.error('Socket не инициализирован.');
      return;
    }

    const chatId = selectedChatTopic?.chatId || selectedUser?.chatId;

    if (!chatId || !botId) {
      console.error('🛑 Не удалось получить chatId или botId для остановки агента', {
        chatId,
        botId,
        selectedUser,
        selectedChatTopic,
      });
      return;
    }

    const payload = {
      chatId: chatId,
      botId: botId,
    };

    console.log('🛑 Отправка события interrupt-thinking с данными:', payload);
    socket.emit('interrupt-thinking', payload);
  };

  const submitEnter = event => {
    if (event.keyCode === 13 && !event.shiftKey) {
      event.preventDefault();
      // Блокируем отправку по Enter если есть thinking сообщения
      if (!hasThinkingMessages) {
        sendMessage();
      }
    }
  };

  const handleCloseModalFiles = () => {
    setFilesData([]);
  };

  const sendMessage = () => {
    // Блокируем отправку если есть thinking сообщения
    if (hasThinkingMessages) {
      console.log('🚫 Отправка заблокирована: бот думает...');
      return;
    }

    if (!value?.trim().length && !filesData.length) return setValue(undefined);

    if (selectedUser) {
      const messageData = {
        userId: selectedUser?.id || selectedUser?.botId,
        content: value,
        chatId: selectedChatTopic?.chatId ?? selectedUser?.chatId,
        replyMessageId: replyMessage?.messageId,
        requestId: generateUUID(),
        files: filesData.map(file => {
          // eslint-disable-next-line
          const { fileBlob, ...rest } = file;
          return { ...rest };
        }),
      };

      // Проверяем, является ли это бот-чатом
      const isBotChat =
        selectedUser?.bot?.botId ||
        selectedUser?.botId ||
        selectedUser?.isBot === true ||
        selectedUser?.userType === 'bot' ||
        selectedChatTopic?.bot?.botId ||
        selectedChatTopic?.isBot === true ||
        selectedChatTopic?.botId;

      // Добавляем modelName только для бот-чатов и если модель выбрана
      if (isBotChat && selectedModelName) {
        messageData.modelName = selectedModelName;
        console.log('📨 Отправляем сообщение боту с выбранной моделью:', selectedModelName);
      } else if (isBotChat) {
        console.log('📨 Отправляем сообщение боту без указания модели (будет использована модель по умолчанию)');
      } else {
        console.log('📨 Отправляем сообщение пользователю (не боту)');
      }

      console.log('📨 Отправляем сообщение:', messageData);
      socket?.emit('sendMessage', messageData);
    }

    textAreaRef.current.value = '';
    setFilesData([]);
    setValue(undefined);
    handleClearReply();
    handleCloseModalFiles();
    adjustTextareaHeight();
    scrollToBottom();
  };

  function scrollToBottom() {
    const chatContainer = containerRef.current;

    if (chatContainer) {
      chatContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const renderFileText = () => {
    if (replyMessage?.files?.length >= 2) {
      return t('files.0');
    } else if (replyMessage?.files?.length === 1) {
      if (replyMessage?.files[0].fileType.includes('image')) {
        return t('files.2');
      } else {
        return t('files.1');
      }
    }
  };

  const handleOnChangeValue = event => {
    setValue(event.target.value);
    adjustTextareaHeight();
  };

  const adjustTextareaHeight = () => {
    const currentTextArea = textAreaRef.current;
    if (currentTextArea) {
      currentTextArea.parentElement.style.height = '36px';
      currentTextArea.style.height = '30px';
      currentTextArea.parentElement.style.height = currentTextArea.scrollHeight + 6 + 'px';
      currentTextArea.style.height = currentTextArea.scrollHeight + 'px';
    }
  };

  const placeholderText = hasThinkingMessages
    ? t('placeholderThinking', 'Бот думает...')
    : botId
      ? t('placeholderMessageBot')
      : t('placeholderMessage');

  return (
    <div className={replyMessage ? classNames(styles.footer, styles.footer_reply) : styles.footer}>
      {replyMessage && (
        <div className={styles.footer__reply}>
          <div className={styles.footer__box}>
            <FiCornerUpLeft />
            <div className={styles.footer__message}>
              <p className={styles.footer__name}>
                {t('replyMessage')}
                {currentUser.id === replyMessage.userId ? (
                  <>
                    {currentUser.firstName} {currentUser.lastName}
                  </>
                ) : (
                  <>
                    {replyMessage.userFirstName} {replyMessage.userLastName}
                  </>
                )}
              </p>
              <p className={styles['footer__message-content']}>
                {replyMessage?.content ? replyMessage.content : renderFileText()}
              </p>
            </div>
          </div>
          <FiX className={styles.footer__icon} onClick={handleClearReply} />
        </div>
      )}
      <div className={styles.footer__content}>
        {/* Не показываем FileInput для бот-чатов */}
        {!(
          selectedUser?.bot?.botId ||
          selectedUser?.botId ||
          selectedUser?.isBot === true ||
          selectedUser?.userType === 'bot' ||
          selectedChatTopic?.bot?.botId ||
          selectedChatTopic?.isBot === true ||
          selectedChatTopic?.botId
        ) && (
          <div
            style={{
              opacity: hasThinkingMessages || isSwitchingChat ? 0.5 : 1,
              pointerEvents: hasThinkingMessages || isSwitchingChat ? 'none' : 'auto',
            }}
          >
            <FileInput setFileData={file => setFilesData([...filesData, file])} />
          </div>
        )}
        <textarea
          maxLength={4096}
          className={styles.footer__textArea}
          placeholder={placeholderText}
          value={filesData?.length ? '' : value}
          ref={textAreaRef}
          onChange={handleOnChangeValue}
          disabled={hasThinkingMessages || isSwitchingChat}
          style={{
            opacity: hasThinkingMessages ? 0.6 : 1,
            cursor: hasThinkingMessages ? 'not-allowed' : 'text',
          }}
          data-qa='messageInput'
        />
        {/*<FiSmile className={styles.footer__icon}/> СМАЙЛИКИ */}
        {hasThinkingMessages ? (
          <FiSquare
            onClick={handleStopAgent}
            className={classNames(styles.footer__icon, styles.footer__icon_stop)}
            style={{
              color: '#ff4d4f',
              opacity: 1, // Активируем кнопку
              cursor: 'pointer',
            }}
            title='Остановить генерацию ответа'
          />
        ) : (
          <AiOutlineSend
            onClick={sendMessage}
            className={styles.footer__icon}
            style={{
              opacity: hasThinkingMessages || isSwitchingChat ? 0.5 : 1,
              cursor: hasThinkingMessages || isSwitchingChat ? 'not-allowed' : 'pointer',
            }}
            data-qa='sendMessageBtn'
          />
        )}
        <ModalWithFile
          visible={!!filesData?.length}
          onClose={handleCloseModalFiles}
          filesData={filesData}
          sendMessage={sendMessage}
          setFilesData={setFilesData}
          valueInput={value}
          setValueInput={e => setValue(e)}
        />
      </div>
    </div>
  );
};

Footer.propTypes = {
  socket: PropTypes.shape({
    emit: PropTypes.func,
  }),
};

export default memo(Footer);
